import _ from 'lodash';
import PieChart from 'components/PieChart';
import { Flex, Margin, Separator } from 'components/base/layout-components';
import Text from 'components/base/Text';
import Icon from 'components/base/Icon';
import { calculatePercentage } from 'common/utils/utils';
import { getColor } from 'common/constants/_colors';
import { PROJECTS, showDecimal } from '../services/projects.services';
import { Box } from '@mui/material';

const FTEContribution = ({
  valueFontSize,
  roundedPeopleAvg,
  avgPerDuration,
  shouldShowAvgPerDuration
}) => (
  <Flex direction='column' minWidth='8rem' pr='small'>
    <Text size={valueFontSize}>{roundedPeopleAvg}</Text>
    <Text pl='0.2rem' size='small' color={PROJECTS.SECONDARY_COLOR}>
      FTE
    </Text>
    {shouldShowAvgPerDuration && (
      <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
        (Avg. per {avgPerDuration})
      </Text>
    )}
  </Flex>
);
const AverageContribution = ({
  valueFontSize,
  roundedPeopleAvg,
  avgPerDuration
}) => (
  <Flex direction='column' minWidth='8rem' pr='small'>
    <Flex items='flex-end'>
      <Text size={valueFontSize}>{roundedPeopleAvg}</Text>
      <Text pl='0.2rem' size='small' color={PROJECTS.SECONDARY_COLOR}>
        People
      </Text>
    </Flex>
    <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
      Avg per {avgPerDuration}
    </Text>
  </Flex>
);

const PeopleEffortSummary = ({
  projectConfig = {},
  project,
  isExtendedView = false,
  isIterationView = false,
  shouldShowAvgPerDuration = true,
  withPieChart = false
}) => {
  const {
    avg_contributors: peopleEffortAvg = null,
    total_contributors: totalNumberOfPeople,
    fte = null
  } = project;
  const isFTECalculation = !!fte;
  const peopleEffort = fte || peopleEffortAvg;
  const peopleAvgPrecent = calculatePercentage(
    peopleEffortAvg,
    totalNumberOfPeople,
    false
  );

  const percent =
    totalNumberOfPeople === 0
      ? 0
      : _.round(peopleAvgPrecent, showDecimal(peopleAvgPrecent));

  const { avgPerDuration } = projectConfig;
  const pieSize = isExtendedView
    ? PROJECTS.LARGE_PIE_SIZE
    : PROJECTS.SMALL_PIE_SIZE;

  const valueFontSize = isExtendedView ? 'large' : 'x-large';
  const roundedPeopleAvg = _.round(peopleEffort, showDecimal(peopleEffort));
  return isIterationView ? (
    <Flex items='baseline' height='2.5rem'>
      <Text size='medium' weight='400' pr='0.3rem'>
        {roundedPeopleAvg}
      </Text>
      <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
        People work on this project:
      </Text>
    </Flex>
  ) : (
    <Flex items='center'>
      {withPieChart && (
        <>
          <Flex items='center'>
            <Box sx={{ width: pieSize.width }}>
              <PieChart
                data={[
                  { value: percent, color: getColor('havelockBlue') },
                  {
                    value: parseInt(100 - percent),
                    color: getColor('botticelli')
                  }
                ]}
                dataKey='value'
                pieSize={pieSize}
                showTooltip={false}
                noStroke={true}
              />
            </Box>
            <Flex pl='normal' direction='column' minWidth='6rem'>
              <Text size={valueFontSize}>{percent}%</Text>

              {isFTECalculation && !isExtendedView ? (
                <>
                  <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
                    Of total work
                  </Text>
                  <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
                    across org
                  </Text>
                </>
              ) : (
                <Text size='small' color={PROJECTS.SECONDARY_COLOR}>
                  of the team
                </Text>
              )}
            </Flex>
          </Flex>
          <Separator
            height='3.5rem'
            margin='0 1.4rem'
            color={getColor('catskillWhite')}
          />
        </>
      )}
      <Flex items='center'>
        {!isExtendedView && (
          <Margin r='small'>
            {isFTECalculation ? (
              <Icon name='PeopleClock' size='large' color='shark' isThin />
            ) : (
              <Icon
                name='teamspeopleicon'
                size='large'
                color={PROJECTS.SECONDARY_COLOR}
                isThin
              />
            )}
          </Margin>
        )}
        {isFTECalculation ? (
          <FTEContribution
            valueFontSize={valueFontSize}
            roundedPeopleAvg={roundedPeopleAvg}
            avgPerDuration={avgPerDuration}
            shouldShowAvgPerDuration={shouldShowAvgPerDuration}
          />
        ) : (
          <AverageContribution
            valueFontSize={valueFontSize}
            roundedPeopleAvg={roundedPeopleAvg}
            avgPerDuration={avgPerDuration}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default PeopleEffortSummary;
